import { io } from "socket.io-client";

class WebSocketManager {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 1000;
    this.maxReconnectDelay = 5000;
    this.heartbeatInterval = null;
    this.connectionHandlers = new Map();
    this.isConnecting = false;
  }

  connect(url, options = {}) {
    if (this.isConnecting) {
      console.log("Connection already in progress...");
      return;
    }

    this.isConnecting = true;

    const defaultOptions = {
      transports: ["websocket", "polling"],
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      maxReconnectionAttempts: 10,
      forceNew: true,
      ...options,
    };

    try {
      this.socket = io(url, defaultOptions);
      this.setupEventHandlers();
      this.startHeartbeat();
      this.isConnecting = false;
      return this.socket;
    } catch (error) {
      console.error("Failed to create WebSocket connection:", error);
      this.isConnecting = false;
      throw error;
    }
  }

  setupEventHandlers() {
    if (!this.socket) return;

    // Connection events
    this.socket.on("connect", () => {
      console.log("✅ WebSocket connected successfully");
      this.reconnectAttempts = 0;
      this.triggerHandlers("connect");
    });

    this.socket.on("disconnect", (reason) => {
      console.log("❌ WebSocket disconnected:", reason);
      this.triggerHandlers("disconnect", reason);

      if (reason === "io server disconnect") {
        // Server disconnected us, try to reconnect
        this.socket.connect();
      }
    });

    this.socket.on("connect_error", (error) => {
      console.error("❌ WebSocket connection error:", error);
      this.triggerHandlers("connect_error", error);
    });

    this.socket.on("reconnect", (attemptNumber) => {
      console.log("🔄 WebSocket reconnected after", attemptNumber, "attempts");
      this.triggerHandlers("reconnect", attemptNumber);
    });

    this.socket.on("reconnect_error", (error) => {
      console.error("❌ WebSocket reconnection error:", error);
      this.triggerHandlers("reconnect_error", error);
    });

    this.socket.on("reconnect_failed", () => {
      console.error("❌ WebSocket reconnection failed");
      this.triggerHandlers("reconnect_failed");
    });

    // Pong response
    this.socket.on("pong", () => {
      console.log("🏓 Received pong from server");
    });
  }

  startHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.connected) {
        this.socket.emit("ping");
        console.log("💓 Sent heartbeat ping");
      }
    }, 30000); // Send ping every 30 seconds
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  on(event, handler) {
    if (!this.connectionHandlers.has(event)) {
      this.connectionHandlers.set(event, []);
    }
    this.connectionHandlers.get(event).push(handler);
  }

  off(event, handler) {
    if (this.connectionHandlers.has(event)) {
      const handlers = this.connectionHandlers.get(event);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  triggerHandlers(event, ...args) {
    if (this.connectionHandlers.has(event)) {
      this.connectionHandlers.get(event).forEach((handler) => {
        try {
          handler(...args);
        } catch (error) {
          console.error(`Error in ${event} handler:`, error);
        }
      });
    }
  }

  emit(event, data) {
    if (this.socket && this.socket.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn(`Cannot emit ${event}: socket not connected`);
    }
  }

  disconnect() {
    this.stopHeartbeat();
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.connectionHandlers.clear();
  }

  isConnected() {
    return this.socket && this.socket.connected;
  }

  getSocket() {
    return this.socket;
  }
}

// Create singleton instance
const webSocketManager = new WebSocketManager();

export default webSocketManager;
